import { useState } from "react";
import { Search, Shield, Clock, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

interface DeletionRequest {
  id: string;
  confirmation_code: string;
  status: string;
  platform: string;
  requested_at: string;
  processed_at: string | null;
  completed_at: string | null;
}

const DataDeletionStatus = () => {
  const [confirmationCode, setConfirmationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [request, setRequest] = useState<DeletionRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!confirmationCode.trim()) {
      setError("Please enter a confirmation code");
      return;
    }

    setLoading(true);
    setError(null);
    setRequest(null);
    setSearched(true);

    try {
      const { data, error: queryError } = await supabase
        .from("data_deletion_requests")
        .select("id, confirmation_code, status, platform, requested_at, processed_at, completed_at")
        .eq("confirmation_code", confirmationCode.trim().toUpperCase())
        .maybeSingle();

      if (queryError) {
        console.error("Query error:", queryError);
        setError("An error occurred while searching. Please try again.");
        return;
      }

      if (!data) {
        setError("No deletion request found with this confirmation code.");
        return;
      }

      setRequest(data);
    } catch (err) {
      console.error("Search error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="h-6 w-6 text-amber-500" />;
      case "processing":
        return <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />;
      case "completed":
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case "failed":
        return <AlertCircle className="h-6 w-6 text-red-500" />;
      default:
        return <Clock className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "processing":
        return "Processing";
      case "completed":
        return "Completed";
      case "failed":
        return "Failed";
      default:
        return status;
    }
  };

  const getStatusDescription = (status: string) => {
    switch (status) {
      case "pending":
        return "Your deletion request has been received and is awaiting processing.";
      case "processing":
        return "We are currently processing your data deletion request.";
      case "completed":
        return "Your data has been successfully deleted from our systems.";
      case "failed":
        return "There was an issue processing your request. Please contact support.";
      default:
        return "Status unknown.";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-10 w-10" />
            <h1 className="text-3xl md:text-4xl font-bold">Data Deletion Status</h1>
          </div>
          <p className="text-primary-foreground/80 text-lg">
            Check the status of your data deletion request
          </p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-2xl">
        {/* Search Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Enter Confirmation Code
            </CardTitle>
            <CardDescription>
              Enter the confirmation code you received when your deletion request was submitted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="e.g., ABC123XYZ"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="font-mono uppercase"
              />
              <Button onClick={handleSearch} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Search</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && searched && (
          <Card className="border-destructive/50 bg-destructive/5 mb-8">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <p className="text-destructive">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result Card */}
        {request && (
          <Card className="border-primary/30">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {getStatusIcon(request.status)}
                  {getStatusText(request.status)}
                </CardTitle>
                <span className="text-xs bg-muted px-2 py-1 rounded font-mono">
                  {request.confirmation_code}
                </span>
              </div>
              <CardDescription>
                {getStatusDescription(request.status)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Platform</p>
                    <p className="font-medium capitalize">{request.platform}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Request ID</p>
                    <p className="font-mono text-xs">{request.id.slice(0, 8)}...</p>
                  </div>
                </div>
                
                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Requested</span>
                    <span>{formatDate(request.requested_at)}</span>
                  </div>
                  {request.processed_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Processing Started</span>
                      <span>{formatDate(request.processed_at)}</span>
                    </div>
                  )}
                  {request.completed_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="text-green-600">{formatDate(request.completed_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <div className="mt-12 space-y-6">
          <h2 className="text-xl font-semibold">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-2">How long does deletion take?</h3>
              <p className="text-sm text-muted-foreground">
                Deletion requests are typically processed within 30 days. 
                You will receive a confirmation once the process is complete.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-2">What data is deleted?</h3>
              <p className="text-sm text-muted-foreground">
                All personal data associated with your account, including messages, 
                contact information, and usage history. Aggregated, anonymized data 
                may be retained for analytics.
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-2">I lost my confirmation code</h3>
              <p className="text-sm text-muted-foreground">
                Contact our support team at{" "}
                <a href="mailto:exclusao@vendas.group" className="text-primary underline">
                  exclusao@vendas.group
                </a>{" "}
                with details about your original request.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center text-sm text-muted-foreground pt-12 border-t mt-12">
          <p>
            <a href="/politicasdeprivacidade" className="text-primary underline">Privacy Policy</a>
            {" · "}
            <a href="/termos" className="text-primary underline">Terms of Service</a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default DataDeletionStatus;
